import { BarChart2 } from "lucide-react";
import { Bar, BarChart, CartesianGrid, LabelList, Rectangle, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const TEAL = "#0D5C59";
const GOLD = "#C4A361";

export const ChartCard = ({ title, subtitle, testId, children, action }) => (
  <section className="ops-card ops-chart-card" data-testid={testId}>
    <div className="ops-card-heading">
      <div>
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

export const EmptyState = ({ icon: Icon = BarChart2, title, message, testId, compact }) => (
  <div className={`ops-empty ${compact ? "is-compact" : ""}`} data-testid={testId}>
    <span className="ops-empty-icon">
      <Icon size={18} />
    </span>
    <strong>{title}</strong>
    {message && <p>{message}</p>}
  </div>
);

const NO_DATA_HEIGHT = 30;

const FillBar = (props) => {
  const { x, width, background, payload } = props;
  if (!payload || payload.positions > 0) return <Rectangle {...props} />;
  const baseline = background.y + background.height;
  return (
    <g data-testid={`fill-rate-no-data-${String(payload.month).toLowerCase()}`}>
      <rect x={x} y={baseline - NO_DATA_HEIGHT} width={width} height={NO_DATA_HEIGHT} rx={4} fill="#FBFBF9" stroke="#B7C4C1" strokeDasharray="4 4" />
      <text x={x + width / 2} y={baseline - NO_DATA_HEIGHT / 2 + 4} textAnchor="middle" fill="#6F807D" fontSize={12} fontWeight={500}>
        No data
      </text>
    </g>
  );
};

const asOfLabel = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const FillRateTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  return (
    <div className="ops-tooltip" data-testid="fill-rate-tooltip">
      <strong>
        {item.month} {item.year}
      </strong>
      {item.positions > 0 ? (
        <>
          <span className="ops-tooltip-value">{item.rate}% fill rate</span>
          <span>
            {item.filled} of {item.positions} positions filled
          </span>
          <span className="ops-tooltip-muted">
            Target {item.target}% · as of {asOfLabel(item.as_of)}
          </span>
        </>
      ) : (
        <>
          <span className="ops-tooltip-value is-muted">No positions recorded</span>
          <span className="ops-tooltip-muted">Not zero — no positions existed as of {asOfLabel(item.as_of)}</span>
        </>
      )}
    </div>
  );
};

export const FillRateChart = ({ series, target }) => {
  const data = series || [];
  const withData = data.filter((item) => item.positions > 0 && item.rate !== null && item.rate !== undefined);
  if (!withData.length) {
    return (
      <EmptyState
        testId="fill-rate-empty"
        title="No positions recorded yet"
        message="Monthly fill rate appears once positions exist for this school year."
      />
    );
  }
  const hasMissing = withData.length !== data.length;
  return (
    <div className="ops-chart" data-testid="fill-rate-bars">
      <div className="ops-chart-plot">
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 640, height: 264 }}>
          <BarChart data={data} margin={{ top: 22, right: 8, left: -14, bottom: 0 }} barCategoryGap="30%">
          <CartesianGrid vertical={false} stroke="#EDF1EE" />
          <XAxis dataKey="month" tick={{ fill: "#596B68", fontSize: 13 }} axisLine={{ stroke: "#DDE4DF" }} tickLine={false} tickMargin={10} />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tickFormatter={(value) => `${value}%`}
            tick={{ fill: "#6F807D", fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip cursor={{ fill: "#F4FAFA" }} content={<FillRateTooltip />} filterNull={false} />
          <ReferenceLine
            y={target}
            stroke={GOLD}
            strokeDasharray="6 5"
            strokeWidth={1.5}
            label={{ value: `Target ${target}%`, position: "insideBottomRight", fill: "#8A5A0B", fontSize: 12, fontWeight: 600 }}
          />
          <Bar dataKey="rate" fill={TEAL} radius={[4, 4, 0, 0]} maxBarSize={72} isAnimationActive={false} shape={<FillBar />}>
            <LabelList
              dataKey="rate"
              position="top"
              offset={8}
              formatter={(value) => (value === null || value === undefined ? "" : `${value}%`)}
              style={{ fill: "#192C2B", fontSize: 13, fontWeight: 600 }}
            />
          </Bar>
        </BarChart>
        </ResponsiveContainer>
      </div>
      <ul className="ops-chart-legend" data-testid="fill-rate-legend">
        <li>
          <i className="is-bar" /> Monthly fill rate
        </li>
        <li>
          <i className="is-target" /> Target {target}%
        </li>
        {hasMissing && (
          <li data-testid="fill-rate-legend-no-data">
            <i className="is-missing" /> No positions recorded
          </li>
        )}
      </ul>
    </div>
  );
};
